import { IconCheck, IconFileDescription } from '@tabler/icons-react';
import { Control, Controller } from 'react-hook-form';

import { useTrackCtaClick } from '@/components/analytics/hooks';
import { Switch } from '@/ui-lib/switch';

import { CartHeading } from '../../../_components/CartLayout';
import { SelectedReportsFormData } from '../../../_components/CheckoutForm';
import { MarketReportGroupVM } from '../../../_components/types';

interface ReportsListProps {
    reports: MarketReportGroupVM[];
    currentSubscriptionItemIds: string[];
    control: Control<SelectedReportsFormData>;

    allIncluded: boolean;
}

export function ReportsList({ reports, currentSubscriptionItemIds, control, allIncluded }: ReportsListProps) {
    const trackCtaClick = useTrackCtaClick('Click', `Market Reports Cart`);
    if (reports.length === 0) return null;
    return (
        <>
            <div className="pb-2 pt-4">
                <CartHeading>Market Reports</CartHeading>
            </div>
            {reports.map((report) => {
                const monthlyPrice = report.price;
                const isCurrentSubscription = currentSubscriptionItemIds.includes(`group-${report.id}`);

                return (
                    <Controller
                        control={control}
                        name="selectedPrices"
                        key={`group-${report.id}`}
                        render={({ field }) => {
                            const isSelected = !!field.value?.[`group-${report.id}`];

                            const isChecked = isSelected;

                            // Reports included with subscription are always "selected" and can't be toggled
                            const isIncludedWithSubscription = report.includedInSubscription;
                            const effectiveChecked = isIncludedWithSubscription || isChecked;

                            return (
                                <div
                                    key={report.id}
                                    data-testid={`report-item-${report.id}`}
                                    className={`flex items-center gap-4 py-3 border-b border-neutral-800 last:border-b-0`}
                                >
                                    {/* Show nothing when all are included; show a checkmark for individually-included
                                      reports, or a toggle for purchasable reports */}
                                    {allIncluded ? null : isIncludedWithSubscription ? (
                                        <div
                                            className="shrink-0 w-11 flex justify-center"
                                            data-testid={`report-switch-${report.id}`}
                                            data-included-in-subscription="true"
                                        >
                                            <div className="w-5 h-5 rounded-full bg-green-600/20 flex items-center justify-center">
                                                <IconCheck size={12} className="text-green-400" strokeWidth={3} />
                                            </div>
                                        </div>
                                    ) : (
                                        <Switch
                                            data-testid={`report-switch-${report.id}`}
                                            data-has-price={monthlyPrice ? 'true' : 'false'}
                                            data-included-in-subscription="false"
                                            checked={effectiveChecked}
                                            onCheckedChange={(checked) => {
                                                const newValue = { ...field.value };
                                                if (checked && monthlyPrice) {
                                                    newValue[`group-${report.id}`] = {
                                                        priceId: monthlyPrice.id,
                                                        type: 'group',
                                                    };
                                                    trackCtaClick(
                                                        `${report.name} added to cart`,
                                                        `/platform-checkout/${monthlyPrice.id}/cart`,
                                                        {
                                                            source: 'Market Reports Cart',
                                                            contentId: report.id,
                                                        }
                                                    );
                                                } else {
                                                    trackCtaClick(`${report.name} removed from cart`, ``, {
                                                        source: 'Market Reports Cart',
                                                        contentId: report.id,
                                                    });
                                                    delete newValue[`group-${report.id}`];
                                                }
                                                field.onChange(newValue);
                                            }}
                                            mode="dark"
                                        />
                                    )}

                                    {/* Report Icon/Thumbnail */}
                                    <div className="shrink-0">
                                        <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center">
                                            <IconFileDescription className="text-neutral-300" size={24} />
                                        </div>
                                    </div>

                                    {/* Report Details */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm text-neutral-100 font-medium">
                                                {report.templateDisplayName ?? report.displayName ?? report.name}
                                            </p>
                                            {isCurrentSubscription && !isIncludedWithSubscription && (
                                                <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded">
                                                    Current
                                                </span>
                                            )}
                                        </div>
                                        {report.description && (
                                            <p className="text-xs text-neutral-400 mt-1 line-clamp-2">
                                                {report.description}
                                            </p>
                                        )}
                                    </div>

                                    {/* Price */}
                                    <div className="shrink-0 text-right">
                                        {isIncludedWithSubscription ? (
                                            <span
                                                className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded"
                                                title="This report is included in your subscription plan at no additional cost"
                                            >
                                                Included with your subscription
                                            </span>
                                        ) : monthlyPrice ? (
                                            <p className="text-sm text-neutral-300">
                                                {monthlyPrice.currency === 'EUR' ? '€' : '$'}
                                                {monthlyPrice.price} / month
                                            </p>
                                        ) : (
                                            <p className="text-xs text-neutral-500">Price not available</p>
                                        )}
                                    </div>
                                </div>
                            );
                        }}
                    />
                );
            })}
        </>
    );
}
